import { useQsFilters } from "@dash/filters";
import { TextField } from "@dash/form";
import { Page, PageHeader } from "@dash/page";

import { ImportItemsOptions, importItemsOptionsSerializer } from "@megaron/catalog-contracts";
import { useClientManager } from "@megaron/react-clients";

import { SyncBar } from "./SyncBar";
import { SyncResultView } from "./SyncResultsView";
import { useGoogleSheetsAccessToken } from "./useGoogleSheetsAccessToken";

export const ImportItemsPage = () => {
  const qs = useQsFilters(importItemsOptionsSerializer, {}, "options");
  const googleSheetsAccessToken = useGoogleSheetsAccessToken(!!qs.filters.googleSheets);
  if (qs.filters.googleSheets) qs.filters.googleSheets.accessToken = googleSheetsAccessToken;

  const query = useClientManager("catalog")
    .importItems()
    .useQuery({
      ...qs.filters,
      dryRun: true,
    });

  const commitMutation = useClientManager("catalog").importItems().useMutation();

  const commit = async () => {
    await commitMutation.mutateAsync({
      importOperations: query.data?.operations ?? [],
      dryRun: false,
    });

    await query.refetch();
  };

  return (
    <Page css={{ maxWidth: "100%", gap: "1rem", display: "flex", flexDirection: "column" }}>
      <PageHeader>Importuj</PageHeader>
      <GoogleSheetsOptionsForm value={qs.filters.googleSheets} onChange={qs.setFilter("googleSheets")} />
      <SyncBar
        onCommit={commit}
        isLoading={query.isFetching || commitMutation.isLoading}
        isDisabled={!query.data?.operations.length}
      />

      <SyncResultView
        isLoading={query.isLoading}
        result={query.data}
        labels={{
          bcId: "Id BC",
          shopifyProductId: "Id produktu Shopify",
          box: "Karton",
          brand: "Marka",
          cost: "Koszt STD",
          defaultProfit: "Domyślna marża",
          description: "Opis",
          gtin: "EAN",
          handle: "Handle",
          isArchived: "Zarchiwizowany?",
          isInAvailability: "Śledzenie dostępności",
          isLoyalty: "Produkt Specjalista",
          itemId: "Id",
          itemName: "Nazwa",
          layer: "Warstwa",
          massKg: "Masa kg",
          pallet: "Paleta",
          productGroup: "Grupa produktowa",
          productId: "Indeks produktu",
          size: "Wielkość",
          sizeUnit: "Jednostka sprzedaży",
          title: "Tytuł",
          variantTitle: "Tytuł wariantu",
          shopifyVariantId: "variantId",
        }}
      />
    </Page>
  );
};

const GoogleSheetsOptionsForm = (props: {
  value: ImportItemsOptions["googleSheets"];
  onChange: (value: ImportItemsOptions["googleSheets"]) => void;
}) => {
  if (!props.value) return null;

  type Form = typeof props.value;

  const onChange =
    <TKey extends keyof Form>(field: TKey) =>
    (value: Form[TKey]) => {
      props.onChange({
        ...props.value!,
        [field]: value,
      });
    };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div css={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
        <TextField label="Id dokumentu" value={props.value.spreadsheetId} onChange={onChange("spreadsheetId")} />
        <TextField
          label="Arkusz"
          value={props.value.sheetTitle ?? ""}
          onChange={(s) => onChange("sheetTitle")(s || undefined)}
        />
      </div>
    </div>
  );
};
